import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';
import { ownerPath } from '@/lib/hosts';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { TourStep } from './types';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT = 200;

function resolveTarget(step: TourStep, isMobile: boolean): string | null {
  if (isMobile && step.targetMobile !== undefined) return step.targetMobile;
  return step.target ?? null;
}

function findTargetElement(target: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
}

function measure(el: HTMLElement): SpotlightRect {
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

interface ProductTourProps {
  steps: TourStep[];
  open: boolean;
  /** Called when the tour ends — finished, skipped or dismissed. */
  onClose: () => void;
}

/**
 * Generic spotlight tour engine (generalisation of `ChatOnboarding`).
 *
 * Steps declare a `data-tour` target; steps whose target is missing from the
 * DOM when the tour opens are silently dropped, and targetless steps render
 * as a centered card. Never blocks: overlay click, Escape and « Passer » all
 * dismiss.
 */
export function ProductTour({ steps, open, onClose }: ProductTourProps) {
  const { t } = useTranslation('tour');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [availableSteps, setAvailableSteps] = useState<TourStep[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);

  // On open: keep only the steps whose target exists right now (centered
  // steps always qualify), so a hidden ChatFab or gated module never leaves
  // the user stuck on an invisible spotlight.
  useEffect(() => {
    if (!open) return;
    const present = steps.filter((step) => {
      const target = resolveTarget(step, isMobile);
      return target === null || findTargetElement(target) !== null;
    });
    setAvailableSteps(present);
    setIndex(0);
  }, [open, steps, isMobile]);

  const currentStep = availableSteps[index];
  const currentTarget = currentStep ? resolveTarget(currentStep, isMobile) : null;

  const updateSpotlight = useCallback(() => {
    if (!currentTarget) {
      setSpotlight(null);
      return;
    }
    const el = findTargetElement(currentTarget);
    setSpotlight(el ? measure(el) : null);
  }, [currentTarget]);

  // Scroll the target into view, then measure (and re-measure on layout moves).
  useEffect(() => {
    if (!open || !currentStep) return;
    const el = currentTarget ? findTargetElement(currentTarget) : null;
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    const timer = setTimeout(updateSpotlight, el ? 320 : 0);
    updateSpotlight();
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [open, currentStep, currentTarget, updateSpotlight, reducedMotion]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  const isLast = index === availableSteps.length - 1;

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const handleAction = (href: string) => {
    handleClose();
    navigate(href === '/pos' ? href : ownerPath(href));
  };

  const tooltipPosition = useMemo(() => {
    if (!spotlight) return null;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const left = Math.max(16, Math.min(spotlight.left, viewportW - TOOLTIP_WIDTH - 16));
    const spaceBelow = viewportH - (spotlight.top + spotlight.height + SPOTLIGHT_PADDING);
    const spaceAbove = spotlight.top - SPOTLIGHT_PADDING;
    let top: number;
    if (spaceBelow >= TOOLTIP_HEIGHT + 20) {
      top = spotlight.top + spotlight.height + SPOTLIGHT_PADDING + 12;
    } else if (spaceAbove >= TOOLTIP_HEIGHT + 20) {
      top = spotlight.top - SPOTLIGHT_PADDING - TOOLTIP_HEIGHT - 12;
    } else {
      // Target spans the full height (e.g. the rail) — place beside it.
      const beside = spotlight.left + spotlight.width + SPOTLIGHT_PADDING + 12;
      if (beside + TOOLTIP_WIDTH + 16 <= viewportW) {
        return { top: Math.max(16, viewportH / 2 - TOOLTIP_HEIGHT / 2), left: beside };
      }
      top = Math.max(16, viewportH / 2 - TOOLTIP_HEIGHT / 2);
    }
    return { top, left };
  }, [spotlight]);

  if (!open || !currentStep) return null;

  const Icon = currentStep.icon;
  const action = currentStep.action;
  const centered = spotlight === null;

  const overlay = (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={t(currentStep.titleKey)}
      className="fixed inset-0 z-[9999]"
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 size-full">
        <defs>
          <mask id="product-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.left - SPOTLIGHT_PADDING}
                y={spotlight.top - SPOTLIGHT_PADDING}
                width={spotlight.width + SPOTLIGHT_PADDING * 2}
                height={spotlight.height + SPOTLIGHT_PADDING * 2}
                rx={14}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#product-tour-mask)"
        />
      </svg>

      {/* Spotlight border ring */}
      {spotlight && (
        <div
          className="pointer-events-none absolute rounded-2xl border-2 border-lime-400/70"
          style={{
            top: spotlight.top - SPOTLIGHT_PADDING,
            left: spotlight.left - SPOTLIGHT_PADDING,
            width: spotlight.width + SPOTLIGHT_PADDING * 2,
            height: spotlight.height + SPOTLIGHT_PADDING * 2,
          }}
        />
      )}

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep.id}
          initial={reducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className={cn(
            'absolute z-10 w-[calc(100vw-32px)] max-w-[320px] rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_18px_36px_-18px_rgba(15,23,42,0.35)]',
            'dark:border-charcoal-700 dark:bg-charcoal-900',
            centered && 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          )}
          style={tooltipPosition ?? undefined}
        >
          <div className="flex items-start gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300">
              <Icon className="size-4.5" strokeWidth={1.75} aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-head text-[15px] font-semibold text-charcoal-950 dark:text-slate-50">
                {t(currentStep.titleKey)}
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                {t(currentStep.descriptionKey)}
              </p>
            </div>
          </div>

          {action && (
            <button
              type="button"
              onClick={() => handleAction(action.href)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-charcoal-950 px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-charcoal-800 dark:bg-slate-50 dark:text-charcoal-950 dark:hover:bg-slate-200"
            >
              {t(action.labelKey)}
            </button>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="flex gap-1" aria-hidden="true">
              {availableSteps.map((s, i) => (
                <span
                  key={s.id}
                  className={cn(
                    'size-1.5 rounded-full transition-colors',
                    i === index ? 'bg-lime-500' : 'bg-slate-300 dark:bg-charcoal-600',
                  )}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {!isLast && (
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-[11px] text-slate-500 transition-colors hover:text-charcoal-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  {t('skip', { defaultValue: 'Passer' })}
                </button>
              )}
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => setIndex((i) => i - 1)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-charcoal-700 transition-colors hover:bg-slate-100 dark:border-charcoal-700 dark:text-slate-200 dark:hover:bg-charcoal-800"
                >
                  {t('previous', { defaultValue: 'Précédent' })}
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="rounded-full bg-lime-400 px-3.5 py-1.5 text-xs font-semibold text-charcoal-950 transition-colors hover:bg-lime-300"
              >
                {isLast
                  ? t('finish', { defaultValue: "C'est parti !" })
                  : t('next', { defaultValue: 'Suivant' })}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );

  return createPortal(overlay, document.body);
}
